import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useDomains } from "@/context/DomainContext";
import Glossy3DCard from "@/components/Glossy3DCard";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Mail, Plus, Users, Trash2, KeyRound, Copy, CheckCircle2, AlertTriangle, RefreshCw, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied"); };

const SERVERS = {
  migadu: { webmail: "https://webmail.migadu.com", imap: "imap.migadu.com", smtp: "smtp.migadu.com", smtpPorts: "465 (SSL) or 587 (STARTTLS)" },
  hostinger: { webmail: "https://mail.hostinger.com", imap: "imap.hostinger.com", smtp: "smtp.hostinger.com", smtpPorts: "465 (SSL)" },
};

const welcomeMessage = (address, password, provider) => {
  const domain = address.split("@")[1] || "";
  const s = SERVERS[provider] || SERVERS.migadu;
  return `Hi,

Your new email account for ${domain} is ready 🎉

• Email address: ${address}
• Password: ${password}

Webmail (sign in from any browser):
  ${s.webmail}

Set it up in a mail app (Outlook / Apple Mail / Gmail app / Thunderbird):
  Incoming mail (IMAP): ${s.imap} — port 993, SSL/TLS
  Outgoing mail (SMTP): ${s.smtp} — port ${s.smtpPorts}
  Username: ${address}
  Password: (the password above)

Tip: please change your password after your first sign-in.`;
};

function CredentialsDialog({ open, onOpenChange, title, rows, provider }) {
  // rows: [{address, password, ok, error}]
  const okRows = rows.filter((r) => r.password);
  const text = okRows.map((r) => `${r.address},${r.password}`).join("\n");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="credentials-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          <DialogDescription>Copy these now — passwords are shown only once and are not stored. Use "Copy message" to send ready-made setup instructions.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.address} className={`rounded-xl p-2.5 text-sm ${r.password ? "bg-emerald-50" : "bg-rose-50"}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-medium text-slate-800">{r.address}</p>
                  {r.password ? (
                    <p className="font-mono text-xs text-slate-600">{r.password}</p>
                  ) : (
                    <p className="text-xs text-rose-600">{r.error || "Failed"}</p>
                  )}
                </div>
              </div>
              {r.password && (
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="outline" className="h-8 rounded-lg bg-white/70 text-xs" onClick={() => copy(`${r.address},${r.password}`)} data-testid={`copy-login-${r.address}`}>
                    <Copy className="mr-1 h-3.5 w-3.5" />Copy login
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 rounded-lg bg-white/70 text-xs" onClick={() => copy(welcomeMessage(r.address, r.password, provider))} data-testid={`copy-message-${r.address}`}>
                    <MessageSquare className="mr-1 h-3.5 w-3.5" />Copy message
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
        {okRows.length > 1 && (
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="outline" className="w-full rounded-xl" onClick={() => copy(text)} data-testid="copy-all-credentials">
              <Copy className="mr-1 h-4 w-4" />Copy all logins ({okRows.length})
            </Button>
            <Button variant="outline" className="w-full rounded-xl" onClick={() => copy(okRows.map((r) => welcomeMessage(r.address, r.password, provider)).join("\n\n————————————\n\n"))} data-testid="copy-all-messages">
              <MessageSquare className="mr-1 h-4 w-4" />Copy all messages
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Mailboxes() {
  const { domains, selectedId } = useDomains();
  const emailDomains = useMemo(() => domains.filter((d) => d.provider === "migadu" || d.provider === "hostinger"), [domains]);
  const [domainId, setDomainId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // single add
  const [addOpen, setAddOpen] = useState(false);
  const [local, setLocal] = useState("");
  const [name, setName] = useState("");

  // bulk
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");

  // results
  const [creds, setCreds] = useState({ open: false, title: "", rows: [] });

  useEffect(() => {
    if (!emailDomains.length) { setDomainId(""); return; }
    setDomainId((prev) => {
      if (prev && emailDomains.some((d) => d.id === prev)) return prev;
      const sel = emailDomains.find((d) => d.id === selectedId);
      return sel ? sel.id : emailDomains[0].id;
    });
  }, [emailDomains, selectedId]);

  const load = async (id) => {
    if (!id) { setRows([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/domains/${id}/mailboxes`);
      setRows(data);
    } catch (e) { toast.error(apiError(e)); setRows([]); } finally { setLoading(false); }
  };

  useEffect(() => { if (domainId) load(domainId); }, [domainId]); // eslint-disable-line

  const addSingle = async () => {
    if (!local.trim()) { toast.error("Enter a mailbox name"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/domains/${domainId}/mailboxes`, { local_part: local.trim(), name: name.trim() });
      setAddOpen(false); setLocal(""); setName("");
      setCreds({ open: true, title: "Mailbox created", rows: [{ address: data.address, password: data.password }] });
      load(domainId);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const bulkCreate = async () => {
    const entries = bulkText.split("\n").map((line) => {
      const [lp, ...rest] = line.split(",");
      const localPart = (lp || "").trim();
      if (!localPart) return null;
      return { local_part: localPart, name: rest.join(",").trim() };
    }).filter(Boolean);
    if (!entries.length) { toast.error("Paste at least one mailbox"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/domains/${domainId}/mailboxes/bulk-create`, { entries });
      setBulkOpen(false); setBulkText("");
      const okCount = data.filter((r) => r.ok).length;
      toast.success(`Created ${okCount}/${data.length} mailboxes`);
      setCreds({ open: true, title: `Bulk create — ${okCount}/${data.length} created`, rows: data });
      load(domainId);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const resetPw = async (m) => {
    try {
      const { data } = await api.put(`/domains/${domainId}/mailboxes/${encodeURIComponent(m.id)}/password`, {});
      setCreds({ open: true, title: "New password", rows: [{ address: m.address || data.address, password: data.password }] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (m) => {
    try {
      await api.delete(`/domains/${domainId}/mailboxes/${encodeURIComponent(m.id)}`);
      toast.success("Mailbox deleted");
      load(domainId);
    } catch (e) { toast.error(apiError(e)); }
  };

  const current = emailDomains.find((d) => d.id === domainId);

  return (
    <div className="animate-fade-up">
      <PageHeader title="Mailboxes" subtitle="Create and manage email mailboxes on Migadu & Hostinger domains" icon={Mail}
        actions={
          <div className="flex items-center gap-2">
            {emailDomains.length > 0 && (
              <>
                <Button variant="outline" className="rounded-xl bg-white/60" onClick={() => load(domainId)} data-testid="refresh-mailboxes">
                  <RefreshCw className="mr-1 h-4 w-4" />Refresh
                </Button>
                <Button variant="outline" className="rounded-xl bg-white/60" onClick={() => setAddOpen(true)} data-testid="add-mailbox-button">
                  <Plus className="mr-1 h-4 w-4" />Add
                </Button>
                <Button className="glossy-emerald relative rounded-xl" onClick={() => setBulkOpen(true)} data-testid="bulk-create-button">
                  <span className="spec-shine" /><Users className="mr-1 h-4 w-4" />Bulk Create
                </Button>
              </>
            )}
          </div>
        }
      />

      {emailDomains.length === 0 ? (
        <div className="glass rounded-3xl p-12 text-center text-slate-500" data-testid="no-migadu-domains">
          No email domains connected yet. Go to <b>Workspace Accounts</b> → Connect and choose <b>Migadu Email</b> or <b>Hostinger Email</b>.
        </div>
      ) : (
        <>
          <div className="mb-4 flex items-center gap-3">
            <Label className="text-sm text-slate-600">Domain</Label>
            <div className="w-72">
              <Select value={domainId} onValueChange={setDomainId}>
                <SelectTrigger className="h-10 rounded-xl border-white/70 bg-white/70" data-testid="mailbox-domain-switcher">
                  <SelectValue placeholder="Select an email domain" />
                </SelectTrigger>
                <SelectContent>
                  {emailDomains.map((d) => (
                    <SelectItem key={d.id} value={d.id} data-testid={`mailbox-domain-${d.domain}`}>{d.label} · {d.domain}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <span className="text-sm text-slate-500">{rows.length} mailbox{rows.length === 1 ? "" : "es"}</span>
          </div>

          <Glossy3DCard className="overflow-hidden p-0" tilt={false} testid="mailboxes-table">
            <div className="relative z-10 divide-y divide-slate-100">
              <div className="grid grid-cols-12 gap-2 bg-white/50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <div className="col-span-5">Address</div>
                <div className="col-span-3">Name</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {loading ? (
                <div className="px-4 py-10 text-center text-slate-400" data-testid="mailboxes-loading">Loading…</div>
              ) : rows.length === 0 ? (
                <div className="px-4 py-10 text-center text-slate-400" data-testid="mailboxes-empty">No mailboxes yet for {current?.domain}.</div>
              ) : rows.map((m) => (
                <div key={m.address} className="grid grid-cols-12 items-center gap-2 px-4 py-3 text-sm" data-testid={`mailbox-row-${m.local_part}`}>
                  <div className="col-span-5 truncate font-medium text-slate-800">{m.address}</div>
                  <div className="col-span-3 truncate text-slate-600">{m.name || "—"}</div>
                  <div className="col-span-2">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {m.is_active ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}{m.is_active ? "active" : "inactive"}
                    </span>
                  </div>
                  <div className="col-span-2 flex justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Reset password" onClick={() => resetPw(m)} data-testid={`reset-pw-${m.local_part}`}>
                      <KeyRound className="h-4 w-4 text-amber-600" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500" title="Delete" data-testid={`delete-mailbox-${m.local_part}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {m.address}?</AlertDialogTitle>
                          <AlertDialogDescription>This permanently deletes the mailbox and all its mail on {current?.provider === "hostinger" ? "Hostinger" : "Migadu"}. This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction className="bg-rose-500 hover:bg-rose-600" onClick={() => del(m)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          </Glossy3DCard>
        </>
      )}

      {/* Single add */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-mailbox-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add mailbox</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Mailbox name (local part)</Label>
              <div className="flex items-center gap-2">
                <Input value={local} onChange={(e) => setLocal(e.target.value)} placeholder="john" data-testid="mailbox-local-input" />
                <span className="text-sm text-slate-500">@{current?.domain}</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Display name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" data-testid="mailbox-name-input" />
            </div>
            <p className="text-xs text-slate-500">A strong password will be generated and shown to you once.</p>
          </div>
          <DialogFooter>
            <Button onClick={addSingle} disabled={busy} className="glossy-emerald relative w-full rounded-xl" data-testid="submit-mailbox-button">
              <span className="spec-shine" />{busy ? "Creating…" : "Create mailbox"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk create */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="bulk-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Bulk create mailboxes</DialogTitle>
            <DialogDescription>One per line. Use <code>localpart</code> or <code>localpart, Full Name</code>. A password is generated for each.</DialogDescription>
          </DialogHeader>
          <Textarea
            value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={10}
            placeholder={"john, John Doe\nsales\nsupport, Support Team"}
            className="font-mono text-sm" data-testid="bulk-create-textarea"
          />
          <DialogFooter>
            <Button onClick={bulkCreate} disabled={busy} className="glossy-emerald relative w-full rounded-xl" data-testid="submit-bulk-create-button">
              <span className="spec-shine" />{busy ? "Creating…" : "Create mailboxes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CredentialsDialog open={creds.open} onOpenChange={(o) => setCreds((c) => ({ ...c, open: o }))} title={creds.title} rows={creds.rows} provider={current?.provider} />
    </div>
  );
}
